import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HeightIcon from '@mui/icons-material/Height';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SpeedIcon from '@mui/icons-material/Speed';
import SportsIcon from '@mui/icons-material/Sports';
import api from '../../api/axiosConfig';

/** Conversion: Feet + Inches -> cm */ 
function feetInchesToCm(feet, inches) {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  const totalInches = f * 12 + i;
  return totalInches * 2.54;
}

/** Conversion: Inches -> cm */
function inchesToCm(inches) {
  const i = parseFloat(inches) || 0;
  return i * 2.54;
}

/** Conversion: lbs -> kg */
function lbsToKg(lbs) {
  const l = parseFloat(lbs) || 0;
  return l * 0.45359237;
}

/**
 * Parse une chaîne de format feet/inches (ex: "6' 2.5"" ou "6'2.5"" ou "6 2.5")
 * Retourne {feet, inches} ou null si format invalide
 */
function parseFeetInchesString(value) {
  if (!value || typeof value !== 'string') return null;

  // Ne pas parser si c'est juste un nombre simple (pour permettre la saisie manuelle)
  // Sauf s'il contient des caractères spéciaux indiquant un format feet/inches
  if (/^[\d.]+$/.test(value.trim())) {
    return null; // C'est juste un nombre, permettre la saisie normale
  }

  // Patterns possibles: 6' 2.5", 6'2.5", 6 2.5, 6ft 2.5in, etc.
  // Regex améliorée pour capturer différents formats
  const patterns = [
    /^(\d+)[''']\s*([\d.]+)[""]?$/,  // 6' 2.5" ou 6' 2.5
    /^(\d+)\s*[''']\s*([\d.]+)\s*[""]?$/,  // 6 ' 2.5 "
    /^(\d+)\s+(\d+\.?\d*)$/,  // 6 2.5 (seulement si deux nombres)
    /^(\d+)ft\s*([\d.]+)in?$/i,  // 6ft 2.5in
    /^(\d+)[''']([\d.]+)[""]$/,  // 6'2.5" sans espace
  ];

  for (const pattern of patterns) {
    const match = value.trim().match(pattern);
    if (match) {
      return {
        feet: match[1],
        inches: match[2]
      };
    }
  }

  // Si c'est un nombre avec apostrophe seule (ex: 6'), c'est feet seulement
  const feetOnly = value.trim().match(/^(\d+)['']$/);
  if (feetOnly) {
    return {
      feet: feetOnly[1],
      inches: '0'
    };
  }

  return null;
}

/** 
 * Gère la saisie restreinte à nombre/flottant (avec ".").
 */
function handleNumericInput(e, allowDecimal = true) {
  let val = e.target.value;
  if (allowDecimal) {
    val = val.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) {
      parts.pop();
      val = parts.join('.');
    }
  } else {
    val = val.replace(/[^0-9]/g, '');
  }
  return val;
}

// Descriptions pour chaque mesure - fonction qui retourne les descriptions traduites
const getMeasurementDescriptions = (t) => ({
  altura: {
    title: t('playerCardCreator.medidasForm.heightWithoutShoes'),
    example: t('playerCardCreator.medidasForm.descriptions.heightExample'),
    description: t('playerCardCreator.medidasForm.descriptions.heightDesc')
  },
  envergadura: {
    title: t('playerCardCreator.medidasForm.wingspan'),
    example: t('playerCardCreator.medidasForm.descriptions.wingspanExample'),
    description: t('playerCardCreator.medidasForm.descriptions.wingspanDesc')
  },
  alcance: {
    title: t('playerCardCreator.medidasForm.standingReach'),
    example: t('playerCardCreator.medidasForm.descriptions.reachExample'),
    description: t('playerCardCreator.medidasForm.descriptions.reachDesc')
  },
  longitudMano: {
    title: t('playerCardCreator.medidasForm.handLength'),
    example: t('playerCardCreator.medidasForm.descriptions.handLengthExample'),
    description: t('playerCardCreator.medidasForm.descriptions.handLengthDesc')
  },
  anchoMano: {
    title: t('playerCardCreator.medidasForm.handWidth'),
    example: t('playerCardCreator.medidasForm.descriptions.handWidthExample'),
    description: t('playerCardCreator.medidasForm.descriptions.handWidthDesc')
  },
  peso: {
    title: t('playerCardCreator.medidasForm.weight'),
    example: t('playerCardCreator.medidasForm.descriptions.weightExample'),
    description: t('playerCardCreator.medidasForm.descriptions.weightDesc')
  },
  grasaCorporal: {
    title: t('playerCardCreator.medidasForm.bodyFat'),
    example: t('playerCardCreator.medidasForm.descriptions.bodyFatExample'),
    description: t('playerCardCreator.medidasForm.descriptions.bodyFatDesc')
  },
  saltoVerticalDePie: {
    title: t('playerCardCreator.medidasForm.standingVertical'),
    example: t('playerCardCreator.medidasForm.descriptions.standingVerticalExample'),
    description: t('playerCardCreator.medidasForm.descriptions.standingVerticalDesc')
  },
  saltoVerticalMaximo: {
    title: t('playerCardCreator.medidasForm.maxVertical'),
    example: t('playerCardCreator.medidasForm.descriptions.maxVerticalExample'),
    description: t('playerCardCreator.medidasForm.descriptions.maxVerticalDesc')
  },
  agilidad: {
    title: t('playerCardCreator.medidasForm.agility'),
    example: t('playerCardCreator.medidasForm.descriptions.agilityExample'),
    description: t('playerCardCreator.medidasForm.descriptions.agilityDesc')
  },
  sprintCuarto: {
    title: t('playerCardCreator.medidasForm.sprint'),
    example: t('playerCardCreator.medidasForm.descriptions.sprintExample'),
    description: t('playerCardCreator.medidasForm.descriptions.sprintDesc')
  }
});

// ✅ SOLUTION 1: Composants sortis du render principal avec React.memo
const CompactInputWithButtons = React.memo(({ value, onChange, onIncrement, onDecrement, inputKey, ...props }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <IconButton
        size="small"
        onClick={onDecrement}
        sx={{
          minWidth: 24,
          height: 24,
          p: 0.25,
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.15) }
        }}
      >
        <RemoveIcon sx={{ fontSize: 12 }} />
      </IconButton>
      <TextField
        key={inputKey}
        value={value}
        onChange={onChange}
        size="small"
        sx={{
          width: 65,
          '& .MuiOutlinedInput-root': {
            height: 28,
            fontSize: '0.8rem'
          }
        }}
        {...props}
      />
      <IconButton
        size="small"
        onClick={onIncrement}
        sx={{
          minWidth: 24,
          height: 24,
          p: 0.25,
          backgroundColor: alpha(theme.palette.success.main, 0.08),
          '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.15) }
        }}
      >
        <AddIcon sx={{ fontSize: 12 }} />
      </IconButton>
    </Box>
  );
});

const CompactMeasurementRow = React.memo(({ measureKey, usFields, metricField, showInfo = false, measurementDescriptions }) => {
  const theme = useTheme();
  const info = measurementDescriptions[measureKey];

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.5,
      px: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02)
      },
      minHeight: 38
    }}>
      <Box sx={{ flex: '0 0 115px', display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
          {info.title}
        </Typography>
        {showInfo && (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" sx={{ mb: 0.25, fontSize: '0.7rem' }}>{info.example}</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{info.description}</Typography>
              </Box>
            }
            arrow
            placement="right"
          >
            <IconButton size="small" sx={{ ml: 0.25, p: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 10 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
        <Box sx={{ flex: '0 0 180px' }}>
          {usFields}
        </Box>
        <Box sx={{ flex: '0 0 120px' }}>
          {metricField}
        </Box>
      </Box>
    </Box>
  );
});

const SectionHeader = React.memo(({ title, icon, sectionKey, expanded, onToggle }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 0.75,
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        borderRadius: 0.5,
        mb: 0.5,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        <Typography variant="subtitle2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
          {title}
        </Typography>
      </Box>
      <IconButton size="small" sx={{ p: 0.25 }}>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Box>
  );
});

const CreateMedidas = ({
  playerId,
  backendUrl,
  section,
  markDirty,
  isSubmitting = false,
  onClose
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Mémoriser les descriptions traduites
  const measurementDescriptions = useMemo(() => getMeasurementDescriptions(t), [t]);

  // Date et evento (obligatoires)
  const today = new Date().toISOString().split('T')[0];
  const [fecha, setFecha] = useState(today);
  const [evento, setEvento] = useState('');

  // États pour gérer les sections ouvertes/fermées - toutes ouvertes en desktop
  const [expandedSections, setExpandedSections] = useState({
    antropometricas: true,
    fuerzaComposicion: true,
    explosividad: true,
    agilidadVelocidad: true
  });

  // Détection mode desktop
  const isDesktop = window.innerWidth >= 1200;

  // --- ÉTATS POUR LES MESURES ---
  // Altura sin zapatos
  const [altura, setAltura] = useState('');
  const [alturaFeet, setAlturaFeet] = useState('');
  const [alturaInches, setAlturaInches] = useState('');


  // Envergadura
  const [envergadura, setEnvergadura] = useState('');
  const [envergaduraFeet, setEnvergaduraFeet] = useState('');
  const [envergaduraInches, setEnvergaduraInches] = useState('');

  // Alcance
  const [alcance, setAlcance] = useState('');
  const [alcanceFeet, setAlcanceFeet] = useState('');
  const [alcanceInches, setAlcanceInches] = useState('');

  // Salto vertical máximo
  const [saltoVerticalMaximo, setSaltoVerticalMaximo] = useState('');
  const [saltoVerticalMaximoInches, setSaltoVerticalMaximoInches] = useState('');

  // Salto vertical de pie
  const [saltoVerticalDePie, setSaltoVerticalDePie] = useState('');
  const [saltoVerticalDePieInches, setSaltoVerticalDePieInches] = useState('');

  // Peso
  const [peso, setPeso] = useState('');
  const [pesoLbs, setPesoLbs] = useState('');

  // Grasa Corporal
  const [grasaCorporal, setGrasaCorporal] = useState('');

  // Longitud Mano
  const [longitudMano, setLongitudMano] = useState('');
  const [longitudManoInches, setLongitudManoInches] = useState('');

  // Ancho Mano
  const [anchoMano, setAnchoMano] = useState('');
  const [anchoManoInches, setAnchoManoInches] = useState('');


  // Agilidad
  const [agilidad, setAgilidad] = useState('');

  // Sprint
  const [sprintCuarto, setSprintCuarto] = useState('');

  // ✅ SOLUTION 1: Handlers stables avec useCallback
  
  // Altura handlers
  const handleAlturaFeetChange = useCallback((e) => {
    const rawValue = e.target.value;

    // Vérifier si c'est un copier-coller de format feet/inches
    const parsed = parseFeetInchesString(rawValue);
    if (parsed) {
      setAlturaFeet(parsed.feet);
      setAlturaInches(parsed.inches);
      markDirty && markDirty();
      return;
    }

    // Sinon, traitement normal
    const value = handleNumericInput(e, true);
    setAlturaFeet(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAlturaFeetIncrement = useCallback(() => {
    const newVal = (parseInt(alturaFeet) || 0) + 1;
    setAlturaFeet(newVal.toString());
    markDirty && markDirty();
  }, [alturaFeet, markDirty]);

  const handleAlturaFeetDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseInt(alturaFeet) || 0) - 1);
    setAlturaFeet(newVal.toString());
    markDirty && markDirty();
  }, [alturaFeet, markDirty]);

  const handleAlturaInchesChange = useCallback((e) => {
    const rawValue = e.target.value;

    // Vérifier si c'est un copier-coller de format feet/inches
    const parsed = parseFeetInchesString(rawValue);
    if (parsed) {
      setAlturaFeet(parsed.feet);
      setAlturaInches(parsed.inches);
      markDirty && markDirty();
      return;
    }

    // Sinon, traitement normal
    const value = handleNumericInput(e, true);
    setAlturaInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAlturaInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(alturaInches) || 0) + 0.25;
    setAlturaInches(newVal.toString());
    markDirty && markDirty();
  }, [alturaInches, markDirty]);

  const handleAlturaInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(alturaInches) || 0) - 0.25);
    setAlturaInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [alturaInches, markDirty]);

  const handleAlturaChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setAltura(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAlturaIncrement = useCallback(() => {
    const newVal = (parseFloat(altura) || 0) + 1;
    setAltura(newVal.toFixed(2));
    markDirty && markDirty();
  }, [altura, markDirty]);

  const handleAlturaDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(altura) || 0) - 1);
    setAltura(newVal.toFixed(2));
    markDirty && markDirty();
  }, [altura, markDirty]);


  // Envergadura handlers
  const handleEnvergaduraFeetChange = useCallback((e) => {
    const rawValue = e.target.value;

    // Vérifier si c'est un copier-coller de format feet/inches
    const parsed = parseFeetInchesString(rawValue);
    if (parsed) {
      setEnvergaduraFeet(parsed.feet);
      setEnvergaduraInches(parsed.inches);
      markDirty && markDirty();
      return;
    }

    // Sinon, traitement normal
    const value = handleNumericInput(e, true);
    setEnvergaduraFeet(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleEnvergaduraFeetIncrement = useCallback(() => {
    const newVal = (parseInt(envergaduraFeet) || 0) + 1;
    setEnvergaduraFeet(newVal.toString());
    markDirty && markDirty();
  }, [envergaduraFeet, markDirty]);

  const handleEnvergaduraFeetDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseInt(envergaduraFeet) || 0) - 1);
    setEnvergaduraFeet(newVal.toString());
    markDirty && markDirty();
  }, [envergaduraFeet, markDirty]);

  const handleEnvergaduraInchesChange = useCallback((e) => {
    const rawValue = e.target.value;

    // Vérifier si c'est un copier-coller de format feet/inches
    const parsed = parseFeetInchesString(rawValue);
    if (parsed) {
      setEnvergaduraFeet(parsed.feet);
      setEnvergaduraInches(parsed.inches);
      markDirty && markDirty();
      return;
    }

    // Sinon, traitement normal
    const value = handleNumericInput(e, true);
    setEnvergaduraInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleEnvergaduraInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(envergaduraInches) || 0) + 0.25;
    setEnvergaduraInches(newVal.toString());
    markDirty && markDirty();
  }, [envergaduraInches, markDirty]);

  const handleEnvergaduraInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(envergaduraInches) || 0) - 0.25);
    setEnvergaduraInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [envergaduraInches, markDirty]);

  const handleEnvergaduraChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setEnvergadura(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleEnvergaduraIncrement = useCallback(() => {
    const newVal = (parseFloat(envergadura) || 0) + 1;
    setEnvergadura(newVal.toFixed(2));
    markDirty && markDirty();
  }, [envergadura, markDirty]);

  const handleEnvergaduraDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(envergadura) || 0) - 1);
    setEnvergadura(newVal.toFixed(2));
    markDirty && markDirty();
  }, [envergadura, markDirty]);

  // Alcance handlers
  const handleAlcanceFeetChange = useCallback((e) => {
    const rawValue = e.target.value;

    // Vérifier si c'est un copier-coller de format feet/inches
    const parsed = parseFeetInchesString(rawValue);
    if (parsed) {
      setAlcanceFeet(parsed.feet);
      setAlcanceInches(parsed.inches);
      markDirty && markDirty();
      return;
    }

    // Sinon, traitement normal
    const value = handleNumericInput(e, true);
    setAlcanceFeet(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAlcanceFeetIncrement = useCallback(() => {
    const newVal = (parseInt(alcanceFeet) || 0) + 1;
    setAlcanceFeet(newVal.toString());
    markDirty && markDirty();
  }, [alcanceFeet, markDirty]);

  const handleAlcanceFeetDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseInt(alcanceFeet) || 0) - 1);
    setAlcanceFeet(newVal.toString());
    markDirty && markDirty();
  }, [alcanceFeet, markDirty]);

  const handleAlcanceInchesChange = useCallback((e) => {
    const rawValue = e.target.value;

    // Vérifier si c'est un copier-coller de format feet/inches
    const parsed = parseFeetInchesString(rawValue);
    if (parsed) {
      setAlcanceFeet(parsed.feet);
      setAlcanceInches(parsed.inches);
      markDirty && markDirty();
      return;
    }

    // Sinon, traitement normal
    const value = handleNumericInput(e, true);
    setAlcanceInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAlcanceInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(alcanceInches) || 0) + 0.25;
    setAlcanceInches(newVal.toString());
    markDirty && markDirty();
  }, [alcanceInches, markDirty]);

  const handleAlcanceInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(alcanceInches) || 0) - 0.25);
    setAlcanceInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [alcanceInches, markDirty]);

  const handleAlcanceChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setAlcance(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAlcanceIncrement = useCallback(() => {
    const newVal = (parseFloat(alcance) || 0) + 1;
    setAlcance(newVal.toFixed(2));
    markDirty && markDirty();
  }, [alcance, markDirty]);

  const handleAlcanceDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(alcance) || 0) - 1);
    setAlcance(newVal.toFixed(2));
    markDirty && markDirty();
  }, [alcance, markDirty]);

  // Longitud mano handlers
  const handleLongitudManoInchesChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setLongitudManoInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleLongitudManoInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(longitudManoInches) || 0) + 0.25;
    setLongitudManoInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [longitudManoInches, markDirty]);

  const handleLongitudManoInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(longitudManoInches) || 0) - 0.25);
    setLongitudManoInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [longitudManoInches, markDirty]);

  const handleLongitudManoChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setLongitudMano(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleLongitudManoIncrement = useCallback(() => {
    const newVal = (parseFloat(longitudMano) || 0) + 0.5;
    setLongitudMano(newVal.toFixed(2));
    markDirty && markDirty();
  }, [longitudMano, markDirty]);

  const handleLongitudManoDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(longitudMano) || 0) - 0.5);
    setLongitudMano(newVal.toFixed(2));
    markDirty && markDirty();
  }, [longitudMano, markDirty]);

  // Ancho mano handlers
  const handleAnchoManoInchesChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setAnchoManoInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAnchoManoInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(anchoManoInches) || 0) + 0.25;
    setAnchoManoInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [anchoManoInches, markDirty]);

  const handleAnchoManoInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(anchoManoInches) || 0) - 0.25);
    setAnchoManoInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [anchoManoInches, markDirty]);

  const handleAnchoManoChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setAnchoMano(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAnchoManoIncrement = useCallback(() => {
    const newVal = (parseFloat(anchoMano) || 0) + 0.5;
    setAnchoMano(newVal.toFixed(2));
    markDirty && markDirty();
  }, [anchoMano, markDirty]);

  const handleAnchoManoDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(anchoMano) || 0) - 0.5);
    setAnchoMano(newVal.toFixed(2));
    markDirty && markDirty();
  }, [anchoMano, markDirty]);

  // Peso handlers
  const handlePesoLbsChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setPesoLbs(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handlePesoLbsIncrement = useCallback(() => {
    const newVal = (parseFloat(pesoLbs) || 0) + 1;
    setPesoLbs(newVal.toFixed(2));
    markDirty && markDirty();
  }, [pesoLbs, markDirty]);

  const handlePesoLbsDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(pesoLbs) || 0) - 1);
    setPesoLbs(newVal.toFixed(2));
    markDirty && markDirty();
  }, [pesoLbs, markDirty]);

  const handlePesoChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setPeso(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handlePesoIncrement = useCallback(() => {
    const newVal = (parseFloat(peso) || 0) + 0.5;
    setPeso(newVal.toFixed(2));
    markDirty && markDirty();
  }, [peso, markDirty]);

  const handlePesoDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(peso) || 0) - 0.5);
    setPeso(newVal.toFixed(2));
    markDirty && markDirty();
  }, [peso, markDirty]);

  // Grasa corporal handlers
  const handleGrasaCorporalChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setGrasaCorporal(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleGrasaCorporalIncrement = useCallback(() => {
    const newVal = (parseFloat(grasaCorporal) || 0) + 0.1;
    setGrasaCorporal(newVal.toFixed(2));
    markDirty && markDirty();
  }, [grasaCorporal, markDirty]);

  const handleGrasaCorporalDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(grasaCorporal) || 0) - 0.1);
    setGrasaCorporal(newVal.toFixed(2));
    markDirty && markDirty();
  }, [grasaCorporal, markDirty]);


  // Salto vertical de pie handlers
  const handleSaltoVerticalDePieInchesChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setSaltoVerticalDePieInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleSaltoVerticalDePieInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(saltoVerticalDePieInches) || 0) + 0.5;
    setSaltoVerticalDePieInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalDePieInches, markDirty]);

  const handleSaltoVerticalDePieInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(saltoVerticalDePieInches) || 0) - 0.5);
    setSaltoVerticalDePieInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalDePieInches, markDirty]);

  const handleSaltoVerticalDePieChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setSaltoVerticalDePie(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleSaltoVerticalDePieIncrement = useCallback(() => {
    const newVal = (parseFloat(saltoVerticalDePie) || 0) + 1;
    setSaltoVerticalDePie(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalDePie, markDirty]);

  const handleSaltoVerticalDePieDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(saltoVerticalDePie) || 0) - 1);
    setSaltoVerticalDePie(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalDePie, markDirty]);

  // Salto vertical maximo handlers
  const handleSaltoVerticalMaximoInchesChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setSaltoVerticalMaximoInches(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleSaltoVerticalMaximoInchesIncrement = useCallback(() => {
    const newVal = (parseFloat(saltoVerticalMaximoInches) || 0) + 0.5;
    setSaltoVerticalMaximoInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalMaximoInches, markDirty]);

  const handleSaltoVerticalMaximoInchesDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(saltoVerticalMaximoInches) || 0) - 0.5);
    setSaltoVerticalMaximoInches(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalMaximoInches, markDirty]);

  const handleSaltoVerticalMaximoChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setSaltoVerticalMaximo(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleSaltoVerticalMaximoIncrement = useCallback(() => {
    const newVal = (parseFloat(saltoVerticalMaximo) || 0) + 1;
    setSaltoVerticalMaximo(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalMaximo, markDirty]);

  const handleSaltoVerticalMaximoDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(saltoVerticalMaximo) || 0) - 1);
    setSaltoVerticalMaximo(newVal.toFixed(2));
    markDirty && markDirty();
  }, [saltoVerticalMaximo, markDirty]);

  // Agilidad handlers
  const handleAgilidadChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setAgilidad(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleAgilidadIncrement = useCallback(() => {
    const newVal = (parseFloat(agilidad) || 0) + 0.01;
    setAgilidad(newVal.toFixed(2));
    markDirty && markDirty();
  }, [agilidad, markDirty]);

  const handleAgilidadDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(agilidad) || 0) - 0.01);
    setAgilidad(newVal.toFixed(2));
    markDirty && markDirty();
  }, [agilidad, markDirty]);

  // Sprint handlers
  const handleSprintCuartoChange = useCallback((e) => {
    const value = handleNumericInput(e, true);
    setSprintCuarto(value);
    markDirty && markDirty();
  }, [markDirty]);

  const handleSprintCuartoIncrement = useCallback(() => {
    const newVal = (parseFloat(sprintCuarto) || 0) + 0.01;
    setSprintCuarto(newVal.toFixed(2));
    markDirty && markDirty();
  }, [sprintCuarto, markDirty]);

  const handleSprintCuartoDecrement = useCallback(() => {
    const newVal = Math.max(0, (parseFloat(sprintCuarto) || 0) - 0.01);
    setSprintCuarto(newVal.toFixed(2));
    markDirty && markDirty();
  }, [sprintCuarto, markDirty]);

  // Toggle section handler
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Fonctions de conversion automatique avec délai pour éviter les interruptions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (alturaFeet || alturaInches) {
        const cm = feetInchesToCm(alturaFeet, alturaInches);
        setAltura(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [alturaFeet, alturaInches]);


  useEffect(() => {
    const timer = setTimeout(() => {
      if (envergaduraFeet || envergaduraInches) {
        const cm = feetInchesToCm(envergaduraFeet, envergaduraInches);
        setEnvergadura(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [envergaduraFeet, envergaduraInches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (alcanceFeet || alcanceInches) {
        const cm = feetInchesToCm(alcanceFeet, alcanceInches);
        setAlcance(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [alcanceFeet, alcanceInches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (longitudManoInches) {
        const cm = inchesToCm(longitudManoInches);
        setLongitudMano(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [longitudManoInches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (anchoManoInches) {
        const cm = inchesToCm(anchoManoInches);
        setAnchoMano(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [anchoManoInches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (saltoVerticalDePieInches) {
        const cm = inchesToCm(saltoVerticalDePieInches);
        setSaltoVerticalDePie(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [saltoVerticalDePieInches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (saltoVerticalMaximoInches) {
        const cm = inchesToCm(saltoVerticalMaximoInches);
        setSaltoVerticalMaximo(cm ? cm.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [saltoVerticalMaximoInches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pesoLbs) {
        const kg = lbsToKg(pesoLbs);
        setPeso(kg ? kg.toFixed(2) : '');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [pesoLbs]);


  const handleSubmit = async () => {
    if (!playerId) {
      alert(t('playerCardCreator.medidasForm.playerIdRequired'));
      return;
    }
    if (!fecha) {
      alert(t('playerCardCreator.medidasForm.dateRequired'));
      return;
    }
    if (!evento) {
      alert(t('playerCardCreator.medidasForm.eventRequired'));
      return;
    }

    try {
      const data = {
        section,
        player_id: playerId,
        fecha,
        fuente: evento,
        altura: altura ? parseFloat(altura) : null,
        altura_con_zapatos: null,
        envergadura: envergadura ? parseFloat(envergadura) : null,
        alcance: alcance ? parseFloat(alcance) : null,
        salto_vertical_maximo: saltoVerticalMaximo
          ? parseFloat(saltoVerticalMaximo)
          : null,
        salto_vertical_de_pie: saltoVerticalDePie
          ? parseFloat(saltoVerticalDePie)
          : null,
        peso: peso ? parseFloat(peso) : null,
        grasa_corporal: grasaCorporal ? parseFloat(grasaCorporal) : null,
        longitud_mano: longitudMano ? parseFloat(longitudMano) : null,
        ancho_mano: anchoMano ? parseFloat(anchoMano) : null,
        press_banca: null,
        agilidad: agilidad ? parseFloat(agilidad) : null,
        sprint_cuarto: sprintCuarto ? parseFloat(sprintCuarto) : null
      };

      await api.post('/api/medidas', data);

      alert(t('playerCardCreator.medidasForm.measureCreated'));
      if (onClose) onClose();
    } catch (error) {
      console.error('Error al crear la medida:', error);
      alert(t('playerCardCreator.medidasForm.measureError'));
    }
  };

  // ✅ SOLUTION 1: Mémoriser les JSX complexes avec useMemo
  const alturaUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="altura-feet"
        placeholder="6"
        value={alturaFeet}
        onChange={handleAlturaFeetChange}
        onIncrement={handleAlturaFeetIncrement}
        onDecrement={handleAlturaFeetDecrement}
        title="Puedes pegar 6' 2.5''"
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>ft</Typography>
      <CompactInputWithButtons
        inputKey="altura-inches"
        placeholder="2.5"
        value={alturaInches}
        onChange={handleAlturaInchesChange}
        onIncrement={handleAlturaInchesIncrement}
        onDecrement={handleAlturaInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [
    alturaFeet, alturaInches,
    handleAlturaFeetChange, handleAlturaFeetIncrement, handleAlturaFeetDecrement,
    handleAlturaInchesChange, handleAlturaInchesIncrement, handleAlturaInchesDecrement
  ]);

  const alturaMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="altura-metric"
        value={altura}
        onChange={handleAlturaChange}
        placeholder="189"
        onIncrement={handleAlturaIncrement}
        onDecrement={handleAlturaDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [altura, handleAlturaChange, handleAlturaIncrement, handleAlturaDecrement]);


  const envergaduraUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="envergadura-feet"
        placeholder="7"
        value={envergaduraFeet}
        onChange={handleEnvergaduraFeetChange}
        onIncrement={handleEnvergaduraFeetIncrement}
        onDecrement={handleEnvergaduraFeetDecrement}
        title="Puedes pegar 7' 2''"
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>ft</Typography>
      <CompactInputWithButtons
        inputKey="envergadura-inches"
        placeholder="2"
        value={envergaduraInches}
        onChange={handleEnvergaduraInchesChange}
        onIncrement={handleEnvergaduraInchesIncrement}
        onDecrement={handleEnvergaduraInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [
    envergaduraFeet, envergaduraInches,
    handleEnvergaduraFeetChange, handleEnvergaduraFeetIncrement, handleEnvergaduraFeetDecrement,
    handleEnvergaduraInchesChange, handleEnvergaduraInchesIncrement, handleEnvergaduraInchesDecrement
  ]);

  const envergaduraMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="envergadura-metric"
        value={envergadura}
        onChange={handleEnvergaduraChange}
        placeholder="218"
        onIncrement={handleEnvergaduraIncrement}
        onDecrement={handleEnvergaduraDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [envergadura, handleEnvergaduraChange, handleEnvergaduraIncrement, handleEnvergaduraDecrement]);

  const alcanceUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="alcance-feet"
        placeholder="8"
        value={alcanceFeet}
        onChange={handleAlcanceFeetChange}
        onIncrement={handleAlcanceFeetIncrement}
        onDecrement={handleAlcanceFeetDecrement}
        title="Puedes pegar 8' 10''"
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>ft</Typography>
      <CompactInputWithButtons
        inputKey="alcance-inches"
        placeholder="10"
        value={alcanceInches}
        onChange={handleAlcanceInchesChange}
        onIncrement={handleAlcanceInchesIncrement}
        onDecrement={handleAlcanceInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [
    alcanceFeet, alcanceInches,
    handleAlcanceFeetChange, handleAlcanceFeetIncrement, handleAlcanceFeetDecrement,
    handleAlcanceInchesChange, handleAlcanceInchesIncrement, handleAlcanceInchesDecrement
  ]);

  const alcanceMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="alcance-metric"
        value={alcance}
        onChange={handleAlcanceChange}
        placeholder="269"
        onIncrement={handleAlcanceIncrement}
        onDecrement={handleAlcanceDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [alcance, handleAlcanceChange, handleAlcanceIncrement, handleAlcanceDecrement]);

  const longitudManoUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="longitud-mano-inches"
        placeholder="9"
        value={longitudManoInches}
        onChange={handleLongitudManoInchesChange}
        onIncrement={handleLongitudManoInchesIncrement}
        onDecrement={handleLongitudManoInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [longitudManoInches, handleLongitudManoInchesChange, handleLongitudManoInchesIncrement, handleLongitudManoInchesDecrement]);

  const longitudManoMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="longitud-mano-metric"
        value={longitudMano}
        onChange={handleLongitudManoChange}
        placeholder="22.9"
        onIncrement={handleLongitudManoIncrement}
        onDecrement={handleLongitudManoDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [longitudMano, handleLongitudManoChange, handleLongitudManoIncrement, handleLongitudManoDecrement]);

  const anchoManoUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="ancho-mano-inches"
        placeholder="8"
        value={anchoManoInches}
        onChange={handleAnchoManoInchesChange}
        onIncrement={handleAnchoManoInchesIncrement}
        onDecrement={handleAnchoManoInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [anchoManoInches, handleAnchoManoInchesChange, handleAnchoManoInchesIncrement, handleAnchoManoInchesDecrement]);

  const anchoManoMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="ancho-mano-metric"
        value={anchoMano}
        onChange={handleAnchoManoChange}
        placeholder="20.3"
        onIncrement={handleAnchoManoIncrement}
        onDecrement={handleAnchoManoDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [anchoMano, handleAnchoManoChange, handleAnchoManoIncrement, handleAnchoManoDecrement]);

  const pesoUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="peso-lbs"
        placeholder="220"
        value={pesoLbs}
        onChange={handlePesoLbsChange}
        onIncrement={handlePesoLbsIncrement}
        onDecrement={handlePesoLbsDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>lbs</Typography>
    </Stack>
  ), [pesoLbs, handlePesoLbsChange, handlePesoLbsIncrement, handlePesoLbsDecrement]);

  const pesoMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="peso-metric"
        value={peso}
        onChange={handlePesoChange}
        placeholder="99.8"
        onIncrement={handlePesoIncrement}
        onDecrement={handlePesoDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>kg</Typography>
    </Stack>
  ), [peso, handlePesoChange, handlePesoIncrement, handlePesoDecrement]);

  const grasaCorporalMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="grasa-corporal"
        value={grasaCorporal}
        onChange={handleGrasaCorporalChange}
        placeholder="6.5"
        onIncrement={handleGrasaCorporalIncrement}
        onDecrement={handleGrasaCorporalDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>%</Typography>
    </Stack>
  ), [grasaCorporal, handleGrasaCorporalChange, handleGrasaCorporalIncrement, handleGrasaCorporalDecrement]);


  const saltoVerticalDePieUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="salto-vertical-de-pie-inches"
        placeholder="32"
        value={saltoVerticalDePieInches}
        onChange={handleSaltoVerticalDePieInchesChange}
        onIncrement={handleSaltoVerticalDePieInchesIncrement}
        onDecrement={handleSaltoVerticalDePieInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [saltoVerticalDePieInches, handleSaltoVerticalDePieInchesChange, handleSaltoVerticalDePieInchesIncrement, handleSaltoVerticalDePieInchesDecrement]);

  const saltoVerticalDePieMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="salto-vertical-de-pie-metric"
        value={saltoVerticalDePie}
        onChange={handleSaltoVerticalDePieChange}
        placeholder="81"
        onIncrement={handleSaltoVerticalDePieIncrement}
        onDecrement={handleSaltoVerticalDePieDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [saltoVerticalDePie, handleSaltoVerticalDePieChange, handleSaltoVerticalDePieIncrement, handleSaltoVerticalDePieDecrement]);

  const saltoVerticalMaximoUSFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="salto-vertical-maximo-inches"
        placeholder="38"
        value={saltoVerticalMaximoInches}
        onChange={handleSaltoVerticalMaximoInchesChange}
        onIncrement={handleSaltoVerticalMaximoInchesIncrement}
        onDecrement={handleSaltoVerticalMaximoInchesDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>in</Typography>
    </Stack>
  ), [saltoVerticalMaximoInches, handleSaltoVerticalMaximoInchesChange, handleSaltoVerticalMaximoInchesIncrement, handleSaltoVerticalMaximoInchesDecrement]);

  const saltoVerticalMaximoMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="salto-vertical-maximo-metric"
        value={saltoVerticalMaximo}
        onChange={handleSaltoVerticalMaximoChange}
        placeholder="96"
        onIncrement={handleSaltoVerticalMaximoIncrement}
        onDecrement={handleSaltoVerticalMaximoDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>cm</Typography>
    </Stack>
  ), [saltoVerticalMaximo, handleSaltoVerticalMaximoChange, handleSaltoVerticalMaximoIncrement, handleSaltoVerticalMaximoDecrement]);

  const agilidadMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="agilidad"
        value={agilidad}
        onChange={handleAgilidadChange}
        placeholder="11.20"
        onIncrement={handleAgilidadIncrement}
        onDecrement={handleAgilidadDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>s</Typography>
    </Stack>
  ), [agilidad, handleAgilidadChange, handleAgilidadIncrement, handleAgilidadDecrement]);

  const sprintCuartoMetricFields = useMemo(() => (
    <Stack direction="row" spacing={1}>
      <CompactInputWithButtons
        inputKey="sprint-cuarto"
        value={sprintCuarto}
        onChange={handleSprintCuartoChange}
        placeholder="3.10"
        onIncrement={handleSprintCuartoIncrement}
        onDecrement={handleSprintCuartoDecrement}
      />
      <Typography variant="caption" sx={{ alignSelf: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>s</Typography>
    </Stack>
  ), [sprintCuarto, handleSprintCuartoChange, handleSprintCuartoIncrement, handleSprintCuartoDecrement]);

  return (
    <Box sx={{ mt: 1 }}>
      {/* En-tête très compact */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('playerCardCreator.medidasForm.date')}
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiInputBase-root': { fontSize: '0.85rem' },
                '& .MuiInputLabel-root': { fontSize: '0.85rem' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label={t('playerCardCreator.medidasForm.event')}
              value={evento}
              onChange={(e) => setEvento(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': { fontSize: '0.85rem' },
                '& .MuiInputLabel-root': { fontSize: '0.85rem' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              size="small"
              sx={{
                height: 32,
                textTransform: 'none',
                fontWeight: 'medium',
                fontSize: '0.8rem',
                flex: 1
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} />
                  {t('playerCardCreator.medidasForm.saving')}
                </>
              ) : (
                t('playerCardCreator.medidasForm.saveMeasurements')
              )}
            </Button>
            {onClose && (
              <Button
                variant="outlined"
                size="small"
                sx={{
                  height: 32,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  minWidth: 80
                }}
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('playerCardCreator.medidasForm.cancel')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Contenedor principal con columnas en desktop */}
      <Grid container spacing={isDesktop ? 1.5 : 1}>
        {/* COLUMNA 1: Medidas Antropométricas */}
        <Grid item xs={12} lg={6}>
          <Box>
            <SectionHeader
              title={t('playerCardCreator.medidasForm.anthropometric')}
              icon={<HeightIcon color="primary" />}
              sectionKey="antropometricas"
              expanded={expandedSections.antropometricas}
              onToggle={() => toggleSection('antropometricas')}
            />
            <Collapse in={expandedSections.antropometricas}>
              <Paper sx={{ mb: 1 }}>
                <CompactMeasurementRow
                  key="altura-row"
                  measureKey="altura"
                  usFields={alturaUSFields}
                  metricField={alturaMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="envergadura-row"
                  measureKey="envergadura"
                  usFields={envergaduraUSFields}
                  metricField={envergaduraMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="alcance-row"
                  measureKey="alcance"
                  usFields={alcanceUSFields}
                  metricField={alcanceMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="longitud-mano-row"
                  measureKey="longitudMano"
                  usFields={longitudManoUSFields}
                  metricField={longitudManoMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="ancho-mano-row"
                  measureKey="anchoMano"
                  usFields={anchoManoUSFields}
                  metricField={anchoManoMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
              </Paper>
            </Collapse>
          </Box>

          {/* PRUEBAS DE EXPLOSIVIDAD en la misma columna */}
          <Box sx={{ mt: isDesktop ? 1 : 1.5 }}>
            <SectionHeader
              title={t('playerCardCreator.medidasForm.explosiveness')}
              icon={<SportsIcon color="primary" />}
              sectionKey="explosividad"
              expanded={expandedSections.explosividad}
              onToggle={() => toggleSection('explosividad')}
            />
            <Collapse in={expandedSections.explosividad}>
              <Paper sx={{ mb: 1 }}>
                <CompactMeasurementRow
                  key="salto-vertical-de-pie-row"
                  measureKey="saltoVerticalDePie"
                  usFields={saltoVerticalDePieUSFields}
                  metricField={saltoVerticalDePieMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="salto-vertical-maximo-row"
                  measureKey="saltoVerticalMaximo"
                  usFields={saltoVerticalMaximoUSFields}
                  metricField={saltoVerticalMaximoMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
              </Paper>
            </Collapse>
          </Box>
        </Grid>

        {/* COLUMNA 2: Fuerza, Composición y Agilidad */}
        <Grid item xs={12} lg={6}>
          <Box>
            <SectionHeader
              title={t('playerCardCreator.medidasForm.strengthComposition')}
              icon={<FitnessCenterIcon color="primary" />}
              sectionKey="fuerzaComposicion"
              expanded={expandedSections.fuerzaComposicion}
              onToggle={() => toggleSection('fuerzaComposicion')}
            />
            <Collapse in={expandedSections.fuerzaComposicion}>
              <Paper sx={{ mb: 1 }}>
                <CompactMeasurementRow
                  key="peso-row"
                  measureKey="peso"
                  usFields={pesoUSFields}
                  metricField={pesoMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="grasa-corporal-row"
                  measureKey="grasaCorporal"
                  usFields={
                    <Chip label={t('playerCardCreator.medidasForm.onlyPercent')} color="info" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  }
                  metricField={grasaCorporalMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
              </Paper>
            </Collapse>
          </Box>

          {/* PRUEBAS DE AGILIDAD Y VELOCIDAD en la misma columna */}
          <Box sx={{ mt: isDesktop ? 1 : 1.5 }}>
            <SectionHeader
              title={t('playerCardCreator.medidasForm.agilitySpeed')}
              icon={<SpeedIcon color="primary" />}
              sectionKey="agilidadVelocidad"
              expanded={expandedSections.agilidadVelocidad}
              onToggle={() => toggleSection('agilidadVelocidad')}
            />
            <Collapse in={expandedSections.agilidadVelocidad}>
              <Paper sx={{ mb: 1 }}>
                <CompactMeasurementRow
                  key="agilidad-row"
                  measureKey="agilidad"
                  usFields={
                    <Chip label={t('playerCardCreator.medidasForm.onlySeconds')} color="info" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  }
                  metricField={agilidadMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
                <CompactMeasurementRow
                  key="sprint-cuarto-row"
                  measureKey="sprintCuarto"
                  usFields={
                    <Chip label={t('playerCardCreator.medidasForm.onlySeconds')} color="info" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  }
                  metricField={sprintCuartoMetricFields}
                  measurementDescriptions={measurementDescriptions}
                />
              </Paper>
            </Collapse>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateMedidas;